import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ExternalLink, Inbox, Loader2, LogOut, MapPin, PenLine, Phone, RefreshCw, User, Users } from "lucide-react";
import api from "@/api";
import { useLang } from "@/context/LanguageContext";
import { LogoMark } from "@/components/Logo";

export default function AdminDashboard() {
  const { t, lang } = useLang();
  const a = t.admin;
  const q = t.quote;
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState(null);
  const [failed, setFailed] = useState(false);
  const editLabel = lang === "ar" ? "تعديل الموقع" : "Edit website";

  const load = () => {
    setQuotes(null);
    setFailed(false);
    api
      .get("/quote")
      .then((r) => setQuotes(r.data))
      .catch(() => setFailed(true));
  };

  useEffect(() => {
    api.get("/auth/me").catch(() => navigate("/admin", { replace: true }));
    load();
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // ignore
    }
    navigate("/admin", { replace: true });
  };

  const fmtDate = (iso) =>
    new Date(iso).toLocaleString(lang === "ar" ? "ar" : "en-GB", { dateStyle: "medium", timeStyle: "short" });

  const studentsLabel = (n) => `${n} ${lang === "ar" ? "طالبًا" : "students"}`;

  return (
    <div className="min-h-screen bg-creamdeep/60" data-testid="admin-dashboard-page">
      <header className="bg-pine text-cream">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <div className="flex items-center gap-3">
            <LogoMark className="h-8 w-8" inverted />
            <span className="font-display text-lg font-bold">{a.dashTitle}</span>
          </div>
          <div className="flex items-center gap-2.5">
            <Link
              to="/admin/edit"
              className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-bold transition-colors hover:border-sun hover:text-sun"
              data-testid="admin-edit-website-button"
            >
              <PenLine className="h-4 w-4" />
              {editLabel}
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-bold transition-colors hover:border-sun hover:text-sun"
              data-testid="admin-view-site-link"
            >
              <ExternalLink className="h-4 w-4" />
              {a.viewSite}
            </Link>
            <button
              onClick={load}
              className="grid h-9 w-9 place-items-center rounded-full border border-white/20 transition-colors hover:border-sun hover:text-sun"
              aria-label="Refresh"
              data-testid="admin-refresh-button"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              onClick={logout}
              className="flex items-center gap-2 rounded-full bg-cream px-4 py-2 text-sm font-bold text-pine transition-colors hover:bg-sun-light"
              data-testid="admin-logout-button"
            >
              <LogOut className="h-4 w-4" />
              {a.signOut}
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        {quotes && quotes.length > 0 && (
          <div className="mb-6 flex items-center gap-3">
            <h1 className="font-display text-2xl font-bold text-ink">{a.dashTitle}</h1>
            <span className="rounded-full bg-sun-light px-3 py-1 text-sm font-bold text-terra" data-testid="admin-request-count">
              {quotes.length} {a.total}
            </span>
          </div>
        )}

        {quotes === null && !failed && (
          <div className="grid place-items-center py-24 text-inksoft" data-testid="admin-loading">
            <Loader2 className="h-8 w-8 animate-spin text-pine" />
            <p className="mt-3 font-semibold">{a.loading}</p>
          </div>
        )}

        {failed && (
          <div className="grid place-items-center rounded-[2rem] border border-cardline bg-white py-20 text-center" data-testid="admin-error">
            <div>
              <p className="font-bold text-terra">{a.error}</p>
              <button onClick={load} className="mt-5 rounded-full bg-pine px-6 py-3 font-bold text-cream hover:bg-pine-dark" data-testid="admin-retry-button">
                {a.retry}
              </button>
            </div>
          </div>
        )}

        {quotes && quotes.length === 0 && (
          <div className="grid place-items-center rounded-[2rem] border border-cardline bg-white py-20 text-center" data-testid="admin-empty-state">
            <div>
              <Inbox className="mx-auto h-12 w-12 text-inkmute" />
              <p className="mt-4 max-w-sm text-inksoft">{a.empty}</p>
            </div>
          </div>
        )}

        {quotes && quotes.length > 0 && (
          <div className="grid gap-5 sm:grid-cols-2">
            {quotes.map((quote) => (
              <div key={quote.id} className="rounded-[1.6rem] border border-cardline bg-white p-6" data-testid="admin-quote-card">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display text-xl font-bold text-ink">{quote.name}</h3>
                    <p className="text-sm font-semibold text-inksoft">{quote.school}</p>
                  </div>
                  <span className="shrink-0 rounded-full bg-sun-light px-3 py-1 text-xs font-bold text-terra">{quote.status === "new" ? a.new : quote.status}</span>
                </div>

                <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold text-inksoft">
                  <span className="flex items-center gap-1.5 rounded-full bg-creamdeep px-3 py-1.5">
                    <Users className="h-3.5 w-3.5 text-pine" />
                    {studentsLabel(quote.students)}
                  </span>
                  <span className="flex items-center gap-1.5 rounded-full bg-creamdeep px-3 py-1.5">
                    <MapPin className="h-3.5 w-3.5 text-pine" />
                    {quote.city}
                  </span>
                  <span className="flex items-center gap-1.5 rounded-full bg-creamdeep px-3 py-1.5">
                    <User className="h-3.5 w-3.5 text-pine" />
                    {q.roles[quote.role] || quote.role}
                  </span>
                </div>

                <a href={`https://wa.me/${quote.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="mt-4 flex w-fit items-center gap-2 text-sm font-bold text-pine hover:underline" dir="ltr">
                  <Phone className="h-4 w-4" />
                  {quote.phone}
                </a>

                {quote.message && (
                  <p className="mt-3 rounded-xl bg-creamdeep/70 p-3 text-sm italic leading-relaxed text-inksoft">
                    <span className="font-bold not-italic">{a.msgLabel}: </span>
                    {quote.message}
                  </p>
                )}

                <p className="mt-4 text-xs text-inkmute">{fmtDate(quote.created_at)}</p>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
